import React, { useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import { toast } from "react-hot-toast";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import { getVendorName } from "../../../utils/getVendorName";
import { isSchemeMembershipEnabled } from "../../../utils/schemeMembershipFlag";
import { useCreateMembershipMutation } from "../../../features/tenant/membership/membershipApi";
import { useGetAppUserTypesMutation } from "../../../features/tenant/appUsers/appUsersApi";
import { useUpdateMembershipByIdMutation } from "../../../features/tenant/membership/membershipApi";
import { useFetchAllSchemeMutation } from "../../../features/tenant/scheme/schemeApi";
import { AiFillDelete } from "react-icons/ai";
import { HiArrowRight, HiOutlineCalendar } from "react-icons/hi";


const rangeTypes = [
  { id: 1, name: "Purchase" },
  { id: 2, name: "Scan" },
  { id: 3, name: "QrMrp" },
  { id: 4, name: "Points" },
];

// Periodic scheme-membership period lengths (mirrors kafka-consumers
// docs-v3/scheme_membership.md §3.1P — 30/45/90/180).
const PERIOD_OPTIONS = [
  { days: 30, label: "30 Days", hint: "Monthly" },
  { days: 45, label: "45 Days", hint: "Mid-cycle" },
  { days: 90, label: "90 Days", hint: "Quarterly" },
  { days: 180, label: "180 Days", hint: "Half-yearly" },
];

const fmtDay = (d) => (d ? String(d).slice(0, 10) : "");

// Pretty date for previews, e.g. "01 Jan 2026".
const prettyDate = (d) =>
  d
    ? new Date(`${fmtDay(d)}T00:00:00Z`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

// Slice [start, end] into fixed periodDays windows (mirrors the backend period
// math: UTC-midnight, last period clamped to scheme end). Returns [] if inputs
// are incomplete/invalid.
const buildPeriods = (start, end, periodDays) => {
  if (!start || !end || !periodDays) return [];
  const MS = 86_400_000;
  const startMs = Date.parse(`${fmtDay(start)}T00:00:00Z`);
  const endInclusiveMs = Date.parse(`${fmtDay(end)}T00:00:00Z`);
  if (isNaN(startMs) || isNaN(endInclusiveMs) || endInclusiveMs < startMs)
    return [];
  const endExclusiveMs = endInclusiveMs + MS; // scheme_end is inclusive
  const periodMs = periodDays * MS;
  const periods = [];
  let s = startMs;
  let idx = 0;
  while (s < endExclusiveMs && periods.length < 60) {
    const e = Math.min(s + periodMs, endExclusiveMs);
    periods.push({
      index: idx,
      start: new Date(s).toISOString().slice(0, 10),
      // display the inclusive last day of the period
      end: new Date(e - MS).toISOString().slice(0, 10),
      partial: e - s < periodMs,
    });
    s = e;
    idx += 1;
  }
  return periods;
};

// rank 1 = highest tier; colours reflect premium level
// index = rank - 1 (rank 1 → index 0 → gold)
const rankColors = [
  { bg: "bg-gradient-to-br from-amber-500 to-yellow-400",   badge: "bg-amber-100 text-amber-800"  },
  { bg: "bg-gradient-to-br from-slate-500 to-slate-400",    badge: "bg-slate-100 text-slate-700"  },
  { bg: "bg-gradient-to-br from-orange-600 to-amber-500",   badge: "bg-orange-100 text-orange-800" },
  { bg: "bg-gradient-to-br from-indigo-500 to-blue-400",    badge: "bg-indigo-100 text-indigo-800" },
  { bg: "bg-gradient-to-br from-teal-500 to-emerald-400",   badge: "bg-teal-100 text-teal-800"    },
  { bg: "bg-gradient-to-br from-rose-500 to-pink-400",      badge: "bg-rose-100 text-rose-800"    },
];

const tierLabel = (rank, total) => {
  if (rank === 1) return "Top Tier";
  if (rank === total) return "Entry Tier";
  return `Tier ${rank}`;
};

const AddMembership = () => {
  // Read at RENDER time: vendorData lands in sessionStorage during login,
  // so a module-level const captures the wrong value on first mount.
  const isSchemeMembership = isSchemeMembershipEnabled();
  const navigate = useNavigate();
  const location = useLocation();

  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedUser, setSelectedUser] = useState({ id: -1, name: "Select User" });
  const [selectedRangeType, setSelectedRangeType] = useState(
    isSchemeMembership
      ? { id: 4, name: "Points" }
      : { id: -1, name: "Select Range Type" },
  );
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemeList, setSchemeList] = useState([]);
  const [tierName, setTierName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [tiers, setTiers] = useState([]);
  // Scheme membership progression: 'lifetime' (all-time points) | 'periodic'
  // (per-period points that reset each period). period_days ∈ {30,45,90,180}.
  const [mode, setMode] = useState("lifetime");
  const [periodDays, setPeriodDays] = useState(30);

  const [getAppUserTypes, { data: appUserTypesData }] =
    useGetAppUserTypesMutation();

  const [addMembership, { data: addMembershipData, error: addMembershipError }] =
    useCreateMembershipMutation();

  const [updateMembership, { data: updateMembershipData, error: updateMembershipError }] =
    useUpdateMembershipByIdMutation();

  const [fetchAllScheme, { data: allSchemeData }] = useFetchAllSchemeMutation();

  useEffect(() => {
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
    getAppUserTypes({ tenant_id: vendorData.tenant_id });
    if (isSchemeMembership) {
      fetchAllScheme({
        token: sessionStorage.getItem("access_token"),
        tenant_id: vendorData.tenant_id,
        body: { limit: 500, offset: 0, status: "1" },
      });
    }
  }, []);

  useEffect(() => {
    if (!location.state) return;
    setIsUpdate(true);
    setStartDate(fmtDay(location.state.start_date));
    setEndDate(fmtDay(location.state.end_date));
    setSelectedRangeType(rangeTypes[Number(location.state.range_type) - 1]);
    setTiers(location.state.tiers);
    if (location.state.mode === "periodic") {
      setMode("periodic");
      if (location.state.period_days) setPeriodDays(Number(location.state.period_days));
    }
    if (isSchemeMembership && location.state.scheme_id) {
      setSelectedScheme({ id: location.state.scheme_id, name: location.state.scheme_name || `Scheme #${location.state.scheme_id}` });
    }
  }, [location.state]);

  // Scheme membership: the run window is the primary scheme's window — auto-fill
  // (and lock) start/end from the selected scheme instead of manual entry.
  useEffect(() => {
    if (!isSchemeMembership || !selectedScheme) return;
    if (selectedScheme.scheme_start) setStartDate(fmtDay(selectedScheme.scheme_start));
    if (selectedScheme.scheme_end) setEndDate(fmtDay(selectedScheme.scheme_end));
  }, [selectedScheme]);

  // sync user type selection after user types load
  useEffect(() => {
    if (!location.state || !appUserTypesData?.body) return;
    const match = appUserTypesData.body.find(
      (u) => u.user_type === location.state.user_type,
    );
    if (match) setSelectedUser(match);
  }, [appUserTypesData, location.state]);

  // populate scheme list and resolve selected scheme name on update
  useEffect(() => {
    const schemes = allSchemeData?.body?.data || [];
    setSchemeList(schemes);
    if (location.state?.scheme_id && schemes.length > 0) {
      const match = schemes.find((s) => s.id === location.state.scheme_id);
      if (match) setSelectedScheme(match);
    }
  }, [allSchemeData]);

  const addTier = (e) => {
    e.preventDefault();
    const trimmed = tierName.trim();
    if (!trimmed) {
      toast.error("Tier name cannot be empty", { id: "noName" });
      return;
    }
    if (tiers.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("A tier with this name already exists", { id: "dupTier" });
      return;
    }
    // newest tier = rank 1 (highest); push existing ranks up
    const shifted = tiers.map((t) => ({ ...t, id: t.id + 1 }));
    setTiers([{ id: 1, name: trimmed }, ...shifted]);
    setTierName("");
  };

  const removeTier = (index) => {
    const updated = tiers
      .filter((_, i) => i !== index)
      .map((t, i) => ({ ...t, id: i + 1 }));
    setTiers(updated);
  };

  const validate = () => {
    if (selectedUser.id === -1) {
      toast.error("Select a user type", { id: "noUser" });
      return false;
    }
    if (!isSchemeMembership && selectedRangeType.id === -1) {
      toast.error("Select a range type", { id: "noRange" });
      return false;
    }
    if (isSchemeMembership && !selectedScheme) {
      toast.error("Select a primary scheme", { id: "noScheme" });
      return false;
    }
    if (
      isSchemeMembership &&
      mode === "periodic" &&
      !PERIOD_OPTIONS.some((p) => p.days === Number(periodDays))
    ) {
      toast.error("Select a valid period length", { id: "noPeriod" });
      return false;
    }
    if (tiers.length === 0) {
      toast.error("Add at least one tier", { id: "noTier" });
      return false;
    }
    if (!startDate) {
      toast.error("Provide a start date", { id: "noStart" });
      return false;
    }
    if (!endDate) {
      toast.error("Provide an end date", { id: "noEnd" });
      return false;
    }
    if (startDate > endDate) {
      toast.error("End date must be after start date", { id: "badDates" });
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    addMembership({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: {
        user_type_id: selectedUser.user_type_id,
        user_type: selectedUser.user_type,
        range_type: selectedRangeType.id,
        tiers,
        start_date: startDate,
        end_date: endDate,
        ...(isSchemeMembership && {
          scheme_id: selectedScheme?.id,
          mode,
          ...(mode === "periodic" && { period_days: Number(periodDays) }),
        }),
      },
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!validate()) return;
    updateMembership({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: {
        user_type_id: selectedUser.user_type_id,
        user_type: selectedUser.user_type,
        range_type: selectedRangeType.id,
        start_date: startDate,
        end_date: endDate,
        ...(isSchemeMembership && {
          scheme_id: selectedScheme?.id,
          mode,
          ...(mode === "periodic" && { period_days: Number(periodDays) }),
        }),
      },
      id: location.state.id,
    });
  };

  useEffect(() => {
    if (addMembershipData) {
      toast.success("Membership created", { id: "membershipCreated" });
      setTimeout(() => navigate(`/genefied/${getVendorName()}/membership`), 400);
    }
    if (addMembershipError) {
      if (addMembershipError.status === 409)
        toast.error("Membership already exists for this user type", { id: "dup409" });
      else toast.error("Something went wrong", { id: "addErr" });
    }
  }, [addMembershipData, addMembershipError]);

  useEffect(() => {
    if (updateMembershipData) {
      toast.success("Membership updated", { id: "membershipUpdated" });
      setTimeout(() => navigate(`/genefied/${getVendorName()}/membership`), 400);
    }
    if (updateMembershipError)
      toast.error("Something went wrong", { id: "updateErr" });
  }, [updateMembershipData, updateMembershipError]);

  // display lowest-to-highest (rank n → rank 1 left-to-right)
  const displayTiers = [...tiers].sort((a, b) => b.id - a.id);

  // Period preview (periodic scheme membership only).
  const periods =
    isSchemeMembership && mode === "periodic"
      ? buildPeriods(startDate, endDate, Number(periodDays))
      : [];

  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme
    .color_shades;

  return (
    <div className="flex w-full bg-gray-100 min-h-full justify-center">
      <div className="w-full">
        {/* Page header */}
        <div className="border-b bg-white border-gray-300 pl-6 py-3 shadow-sm text-xl font-bold">
          {isUpdate
            ? isSchemeMembership
              ? "Update Scheme Membership"
              : "Update Membership"
            : isSchemeMembership
            ? "Add New Scheme Membership"
            : "Add New Membership"}
          <LinksComponent />
        </div>

        <div className="p-4 space-y-4">

          {/* ── Section 1: Configuration ── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Configuration
              </h2>
              {isSchemeMembership && (
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                    mode === "periodic"
                      ? "bg-violet-50 text-violet-700 border-violet-200"
                      : "bg-blue-50 text-blue-700 border-blue-200"
                  }`}
                >
                  {mode === "periodic"
                    ? `Periodic · ${periodDays}-day periods`
                    : "Lifetime Scan Points"}
                </span>
              )}
            </div>
            <div className="p-6 space-y-6">
              <div
                className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${
                  isSchemeMembership ? "lg:grid-cols-3" : "lg:grid-cols-4"
                }`}
              >
                {/* User Type */}
                <div>
                  {appUserTypesData?.body ? (
                    <SelectComponent
                      title="User Type"
                      placeholder="Select user type…"
                      selected={selectedUser}
                      setSelected={setSelectedUser}
                      dataArr={appUserTypesData.body}
                    />
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">User Type</p>
                      <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                    </div>
                  )}
                </div>

                {/* Range Metric — only shown for regular membership */}
                {!isSchemeMembership && (
                  <div>
                    <SelectComponent
                      title="Range Metric"
                      selected={selectedRangeType}
                      setSelected={setSelectedRangeType}
                      dataArr={rangeTypes}
                    />
                  </div>
                )}

                {/* Primary Scheme — only shown for scheme membership */}
                {isSchemeMembership && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Scheme <span className="text-red-500">*</span>
                    </label>
                    {schemeList.length > 0 ? (
                      <select
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                        value={selectedScheme?.id ?? ""}
                        onChange={(e) => {
                          const scheme = schemeList.find(
                            (s) => String(s.id) === e.target.value,
                          );
                          setSelectedScheme(scheme || null);
                        }}
                      >
                        <option value="">Select primary scheme…</option>
                        {schemeList
                          .filter((s) => s.is_primary === true || s.is_primary === "true")
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              #{s.id} — {s.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                    )}
                    {selectedScheme && (
                      <p className="mt-1 text-xs text-blue-600 font-medium">
                        Scheme ID: {selectedScheme.id}
                      </p>
                    )}
                  </div>
                )}

                {/* Scheme membership: derived (non-editable) run window, aligned as
                    the third column — read-only text, not a form field. */}
                {isSchemeMembership && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      Membership Window
                    </p>
                    {selectedScheme && startDate && endDate ? (
                      <div className="flex items-center gap-2 py-2 text-sm">
                        <HiOutlineCalendar className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="font-semibold text-gray-800">
                          {prettyDate(startDate)}
                        </span>
                        <HiArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                        <span className="font-semibold text-gray-800">
                          {prettyDate(endDate)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 py-2 text-sm text-gray-400 italic">
                        <HiOutlineCalendar className="w-4 h-4 shrink-0" />
                        Select a scheme
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400">
                      Auto-filled from the selected scheme
                    </p>
                  </div>
                )}

                {/* Regular membership keeps editable dates. */}
                {!isSchemeMembership && (
                  <>
                    <div>
                      <InputComponent
                        name="Start Date"
                        type="date"
                        required
                        value={startDate}
                        setValue={setStartDate}
                      />
                    </div>
                    <div>
                      <InputComponent
                        name="End Date"
                        type="date"
                        required
                        value={endDate}
                        setValue={setEndDate}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ── Progression Mode (scheme membership only) ── */}
              {isSchemeMembership && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5 space-y-5">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-gray-700">
                      Progression Mode
                    </p>
                    <p className="text-xs text-gray-500">
                      {mode === "periodic"
                        ? "Points and tier reset each period — members must re-earn their slab every period."
                        : "Tier progresses on all-time scan points across the whole scheme window."}
                    </p>
                  </div>

                  {/* Segmented toggle */}
                  <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
                    {[
                      { key: "lifetime", label: "Lifetime" },
                      { key: "periodic", label: "Periodic" },
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setMode(opt.key)}
                        className={`px-5 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          mode === opt.key
                            ? "text-white shadow-sm"
                            : "text-gray-600 hover:text-gray-800"
                        }`}
                        style={
                          mode === opt.key
                            ? { backgroundColor: primaryColor["700"] }
                            : {}
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Period length + preview */}
                  {mode === "periodic" && (
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                      {/* Period length dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Period Length <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none bg-white"
                          value={periodDays}
                          onChange={(e) => setPeriodDays(Number(e.target.value))}
                        >
                          {PERIOD_OPTIONS.map((p) => (
                            <option key={p.days} value={p.days}>
                              {p.label} · {p.hint}
                            </option>
                          ))}
                        </select>
                        {periods.length > 0 && (
                          <p className="mt-2 text-xs text-gray-500">
                            Scheme window splits into{" "}
                            <span className="font-semibold text-violet-700">
                              {periods.length}
                            </span>{" "}
                            period{periods.length > 1 ? "s" : ""}.
                          </p>
                        )}
                      </div>

                      {/* Period timeline preview */}
                      <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Period Schedule
                        </label>
                        {!selectedScheme ? (
                          <div className="h-[92px] flex items-center justify-center text-xs text-gray-400 border-2 border-dashed border-gray-200 rounded-lg bg-white">
                            Select a primary scheme to preview periods
                          </div>
                        ) : periods.length === 0 ? (
                          <div className="h-[92px] flex items-center justify-center text-xs text-gray-400 border-2 border-dashed border-gray-200 rounded-lg bg-white">
                            No valid period window for the selected scheme
                          </div>
                        ) : (
                          <div className="max-h-[160px] overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                            {periods.map((p) => (
                              <div
                                key={p.index}
                                className="flex items-center justify-between px-3 py-2 text-xs"
                              >
                                <span className="flex items-center gap-2">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 font-semibold text-[11px]">
                                    {p.index + 1}
                                  </span>
                                  <span className="font-medium text-gray-700">
                                    Period {p.index + 1}
                                  </span>
                                  {p.partial && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                      partial
                                    </span>
                                  )}
                                </span>
                                <span className="text-gray-500 font-mono">
                                  {prettyDate(p.start)} – {prettyDate(p.end)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Section 2: Tier Hierarchy ── */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Tier Hierarchy
                </h2>
                {!isUpdate && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Add tiers from lowest to highest — each new tier you add becomes the top tier.
                  </p>
                )}
              </div>
              {tiers.length > 0 && (
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {tiers.length} tier{tiers.length > 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="p-6 space-y-5">
              {/* Add Tier input — create mode only */}
              {!isUpdate && (
                <div className="flex items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Tier Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g. Silver, Gold, Platinum…"
                      value={tierName}
                      onChange={(e) => setTierName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addTier(e);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addTier}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors"
                    style={{ backgroundColor: primaryColor["700"] }}
                  >
                    + Add Tier
                  </button>
                </div>
              )}

              {/* Tier cards — sorted lowest → highest (rank n → rank 1) */}
              {tiers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                  <svg className="w-10 h-10 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-sm">No tiers added yet</p>
                  <p className="text-xs mt-1">Enter a tier name above and click "Add Tier"</p>
                </div>
              ) : (
                <>
                  {/* Progression flow */}
                  <div className="flex flex-wrap items-center gap-2">
                    {displayTiers.map((tier, idx) => {
                      const colorIdx = Math.min(tier.id - 1, rankColors.length - 1);
                      const color = rankColors[colorIdx];
                      const isTopTier = tier.id === 1;
                      const isEntryTier = tier.id === tiers.length;
                      const originalIdx = tiers.findIndex((t) => t.id === tier.id);

                      return (
                        <React.Fragment key={tier.id}>
                          <div className="relative group">
                            {/* Card */}
                            <div
                              className={`${color.bg} rounded-xl p-4 min-w-[120px] text-white shadow-md transition-transform hover:scale-105`}
                            >
                              {/* Delete button */}
                              {!isUpdate && (
                                <button
                                  type="button"
                                  onClick={() => removeTier(originalIdx)}
                                  className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity border border-red-200"
                                  title="Remove tier"
                                >
                                  <AiFillDelete className="w-3.5 h-3.5" />
                                </button>
                              )}

                              {/* Rank badge */}
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">
                                  Rank {tier.id}
                                </span>
                                {isTopTier && (
                                  <span className="text-xs">⭐</span>
                                )}
                              </div>

                              {/* Tier name */}
                              <p className="font-bold text-base leading-tight">{tier.name}</p>

                              {/* Level label */}
                              <p className="text-xs mt-1 text-white/80">
                                {tierLabel(tier.id, tiers.length)}
                              </p>
                            </div>
                          </div>

                          {/* Arrow between cards */}
                          {idx < displayTiers.length - 1 && (
                            <HiArrowRight className="text-gray-400 w-5 h-5 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 pt-1">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />
                      Left = lowest tier
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                      Right = highest tier (Rank 1)
                    </span>
                    {!isUpdate && (
                      <span className="ml-auto text-gray-300 italic">
                        Hover a card to delete it
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Save / Update button ── */}
          <div className="flex justify-end pb-4">
            {isUpdate ? (
              <button
                type="button"
                onClick={handleUpdate}
                className="px-8 py-2.5 text-sm font-semibold text-white rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                style={{ backgroundColor: primaryColor["700"] }}
              >
                Update Membership
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-8 py-2.5 text-sm font-semibold text-white rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                style={{ backgroundColor: primaryColor["700"] }}
              >
                Save Membership
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddMembership;
